import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import Circle from '../assigner-circle/assigner-circle';
import WaveAnimation, { WaveModes, WaveColors } from '../../../components/wave/wave-animation';

interface Props {
	areChosenVehiclesHaveMatchSkills: boolean;
	tasksWeight: number | undefined;
	totalVehiclesWeight: number;
	teamHaveTrailer?: boolean;
}

const FIVE_PERCENT = 5;
const FIFTY_PERCENT = 50;
const VehicleAssignerModalHeader: React.FC<Props> = ({
	areChosenVehiclesHaveMatchSkills,
	tasksWeight,
	totalVehiclesWeight,
	teamHaveTrailer
}: Props) => {
	const { t } = useTranslation();
	const [waveMode, setWaveMode] = useState<WaveModes>(WaveModes.LITTLE);
	const [waveColor, setWaveColor] = useState<WaveColors>(WaveColors.RED);
	const freeWeightInVehicle = totalVehiclesWeight - (tasksWeight || 0);
	const freeWeightInPercentage = (freeWeightInVehicle / totalVehiclesWeight) * 100;
	const orangeCondition = freeWeightInPercentage >= FIFTY_PERCENT;
	const redCondition = tasksWeight > totalVehiclesWeight;
	const yellowCondition =
		totalVehiclesWeight > tasksWeight &&
		freeWeightInPercentage > FIVE_PERCENT &&
		freeWeightInPercentage < FIFTY_PERCENT;
	const greenCondition = totalVehiclesWeight >= tasksWeight && freeWeightInPercentage <= FIVE_PERCENT;

	useEffect(() => {
		if (!totalVehiclesWeight) {
			setWaveMode(WaveModes.HIDE);
			return;
		}

		if (orangeCondition) {
			setWaveColor(WaveColors.ORANGE);
			setWaveMode(WaveModes.LITTLE);
		} else if (redCondition) {
			setWaveColor(WaveColors.RED);
			setWaveMode(WaveModes.FULL);
		} else if (yellowCondition) {
			setWaveColor(WaveColors.YELLOW);
			setWaveMode(WaveModes.HALF);
		} else if (greenCondition) {
			setWaveColor(WaveColors.GREEN);
			setWaveMode(WaveModes.FULL);
		}
	}, [totalVehiclesWeight]);

	return (
		<div className="vehicle-modal-header">
			<div className="vehicle-modal-header-description">
				<span>{t('ROUTE_VEHICLE_ASSIGNMENT_MODAL.DESCRIPTION')}.</span>
				<span>{teamHaveTrailer && `${t('ROUTE_VEHICLE_ASSIGNMENT_MODAL.TRAILER_DESCRIPTION')}.`}</span>
			</div>

			<div className="circles-info">
				<Circle
					className={classNames('skills-circle', {
						'red-border': !areChosenVehiclesHaveMatchSkills && areChosenVehiclesHaveMatchSkills !== null,
						'green-border': areChosenVehiclesHaveMatchSkills
					})}
					content={
						areChosenVehiclesHaveMatchSkills === null ? (
							t('NOT_AVAILABLE')
						) : (
							<BringgIcon
								iconName={
									areChosenVehiclesHaveMatchSkills ? BringgFontIcons.Selected : BringgFontIcons.Close
								}
								className={classNames('big-font', {
									'green-text': areChosenVehiclesHaveMatchSkills,
									'red-text': !areChosenVehiclesHaveMatchSkills
								})}
							/>
						)
					}
					description={t('ROUTE_VEHICLE_ASSIGNMENT_MODAL.SKILLS')}
				/>

				<Circle
					className={classNames('capacity-circle', {
						'orange-border': orangeCondition,
						'red-border': redCondition,
						'yellow-border': yellowCondition,
						'green-border': greenCondition
					})}
					content={
						<WaveAnimation
							waveColor={waveColor}
							waveMode={waveMode}
							content={`${tasksWeight || t('NOT_AVAILABLE')} / ${
								totalVehiclesWeight || t('NOT_AVAILABLE')
							}`}
						/>
					}
					description={t('ROUTE_VEHICLE_ASSIGNMENT_MODAL.CAPACITY')}
				/>
			</div>
		</div>
	);
};

export default VehicleAssignerModalHeader;
